import { Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Observable, finalize, switchMap, tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { copy } from '../../../../Common/Misc/helpers/copy/copy.helper';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SortOrderEnum } from '../../../../api/shared/enums/sort-order.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';

import { ApiKeyService } from '../../services/api-key.service';
import { ApiKey } from '../../models/api-key';

enum SourceSortOptionEnum {
    name,
    createdDate,
    status
}

@Component({
    selector: 'agpr-manage-api-keys-dialog',
    templateUrl: './manage-api-keys-dialog.component.html',
    styleUrls: ['./manage-api-keys-dialog.component.scss'],
    standalone: false
})
export class ManageApiKeysDialogComponent extends AbstractDialogComponent implements OnInit {
    readonly buttonTypes = ButtonTypesEnum;
    readonly sortOrder = SortOrderEnum;
    readonly sourceGroupSortMenuOptions: DropdownOptionModel[] = [
        {
            label: 'Source.manageDialog.sortOptions.name',
            value: SourceSortOptionEnum.name
        },
        {
            label: 'Source.manageDialog.sortOptions.createdDate',
            value: SourceSortOptionEnum.createdDate
        },
        {
            label: 'Source.manageDialog.sortOptions.status',
            value: SourceSortOptionEnum.status
        },
    ];

    @Input() id: string = 'manageApiKeysDialog';
    @Input() animationTrigger: Observable<FullProfileAnimationModel> | null = null;
    @Input() overlayContainer: Element | null = null;

    @ViewChild('keyListScrollPanel') keyListScrollPanel: ScrollPanelComponent;
    @ViewChildren('keyItem') keyItems: QueryList<ElementRef>;

    isLoading: boolean = false;
    hideSlideOverlay: boolean = false;
    apiKeys: ApiKey[] = [];
    selectedKey: ApiKey | null = null;
    showKeyValue: boolean = false;
    isCreating: boolean = false;
    newKeyName: string = '';
    sourceGroupSortOrder: SortOrderEnum = SortOrderEnum.ascending;
    selectedSourceGroupSortOption: DropdownOptionModel = this.sourceGroupSortMenuOptions[0];

    constructor(
        coreDialogService: CoreDialogService,
        private apiKeyService: ApiKeyService,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'ManageApiKeysDialogComponent');
    }

    ngOnInit(): void {
        this.loadApiKeys();
        this.sortSourceGroups(this.sourceGroupSortMenuOptions[1]);
    }

    private loadApiKeys(): void {
        this.isLoading = true;
        this.apiKeyService.getApiKeys().pipe(
            finalize(() => this.isLoading = false)
        ).subscribe(keys => {
            this.apiKeys = keys ?? [];
            if (this.apiKeys.length > 0) {
                this.selectedKey = this.apiKeys[0];
            }
        });
    }

    private scrollKeyIntoView(id: number) {
        const key = this.apiKeys.find(k => k.id === id);
        if (!key) {
            return;
        }

        const element = this.keyItems.find(
            el => +el.nativeElement.dataset.id === key.id
        );
        if (element) {
            this.keyListScrollPanel.scrollIntoViewNative(element.nativeElement);
        }
    }

    selectKey(key: ApiKey): void {
        this.selectedKey = key.clone();
        this.isCreating = false;
        this.showKeyValue = false;
    }

    startCreate(): void {
        this.isCreating = true;
        this.selectedKey = null;
        this.newKeyName = '';
        this.showKeyValue = false;
    }

    createKey(): void {
        const keyName = this.newKeyName.trim();
        if (!keyName) {
            return;
        }

        this.apiKeyService.createApiKey(keyName).pipe(
            switchMap(apiKeyApiModel => this.apiKeyService.getApiKeys().pipe(
                tap(apiKeys => {
                    this.apiKeys = apiKeys ?? [];
                    this.sortSourceGroups();
                    const newKey = apiKeys.find(key => key.keyValue === apiKeyApiModel.apiKey);
                    if (newKey) {
                        this.selectKey(newKey);
                        requestAnimationFrame(() => this.scrollKeyIntoView(newKey.id));
                    }
                })
            ))
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary:'AMT.apiKeys.management.create.growlMsg.success.heading',
                    detail: this.localizationService.transform('AMT.apiKeys.management.create.growlMsg.success.message', { name: keyName })
                });
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary:'AMT.apiKeys.management.create.growlMsg.error.heading',
                    detail: this.localizationService.transform('AMT.apiKeys.management.create.growlMsg.error.message', { name: keyName })
                });
            }
        });
    }

    toggleKeyVisibility(): void {
        this.showKeyValue = !this.showKeyValue;
    }

    copyKey(): void {
        if (this.selectedKey) {
            copy.text(this.selectedKey.keyValue);
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: this.localizationService.transform('AMT.apiKeys.management.copy.growlMsg.heading'),
                detail: this.localizationService.transform('AMT.apiKeys.management.copy.growlMsg.message'),
            });
        }
    }

    updateKey(): void {
        if (!this.selectedKey?.name?.trim()) {
            return;
        }
        const key = this.selectedKey.clone();
        this.apiKeyService.updateApiKey(key).pipe(
            switchMap(() => this.apiKeyService.getApiKeys().pipe(
                tap(apiKeys => {
                    this.apiKeys = apiKeys ?? [];
                    this.sortSourceGroups();
                    const updatedKey = apiKeys.find(k => k.keyValue === key.keyValue);
                    if (updatedKey) {
                        this.selectKey(updatedKey);
                    }
                })
            ))
        ).subscribe({
            next: () => {
                const idx = this.apiKeys.findIndex(k => k.id === key.id);
                if (idx !== -1) {
                    this.apiKeys.splice(idx, 1, key);
                }
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'AMT.apiKeys.management.update.growlMsg.success.heading',
                    detail: this.localizationService.transform('AMT.apiKeys.management.update.growlMsg.success.message', { name: key.name })
                });
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'AMT.apiKeys.management.update.growlMsg.error.heading',
                    detail: this.localizationService.transform('AMT.apiKeys.management.update.growlMsg.error.message', { name: key.name })
                });
            }
        });
    }

    updateKeyStatus(apiKey: ApiKey): void {
        const key = apiKey.clone();
        key.disabled = !apiKey.disabled;

        this.apiKeyService.updateApiKeyStatus(key).subscribe({
            next: () => {
                const idx = this.apiKeys.findIndex(k => k.id === key.id);
                if (idx > -1) {
                    this.apiKeys.splice(idx, 1, key);
                }

                if (this.selectedKey?.id === key.id) {
                    this.selectedKey.disabled = key.disabled;
                }

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: `AMT.apiKeys.management.${key.disabled ? 'suspend' : 'activate'}.growlMsg.success.heading`,
                    detail: this.localizationService.transform(
                        `AMT.apiKeys.management.${key.disabled ? 'suspend' : 'activate'}.growlMsg.success.message`,
                        { name: key.name }
                    )
                });
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: `AMT.apiKeys.management.${key.disabled ? 'suspend' : 'activate'}.growlMsg.error.heading`,
                    detail: this.localizationService.transform(
                        `AMT.apiKeys.management.${key.disabled ? 'suspend' : 'activate'}.growlMsg.error.message`,
                        { name: key.name }
                    )
                });
            }
        });
    }

    toggleSourceGroupSortOrder() {
        this.sourceGroupSortOrder = this.sourceGroupSortOrder === SortOrderEnum.ascending
            ? SortOrderEnum.descending
            : SortOrderEnum.ascending;
        this.sortSourceGroups();
    }

    sortSourceGroups(option?: DropdownOptionModel) {
        if (option) {
            this.selectedSourceGroupSortOption = option;
        }

        this.apiKeys.sort((a, b) => {
            if (this.selectedSourceGroupSortOption.value === SourceSortOptionEnum.name) {
                return a.name.trim().localeCompare(b.name.trim());
            } else if (this.selectedSourceGroupSortOption.value === SourceSortOptionEnum.createdDate) {
                return a.createdDate.valueOf() - b.createdDate.valueOf();
            } else if (this.selectedSourceGroupSortOption.value === SourceSortOptionEnum.status) {
                return (a.disabled === b.disabled) ? 0 : a.disabled ? 1 : -1;
            }
            return 0;
        });

        if (this.sourceGroupSortOrder !== SortOrderEnum.ascending) {
            this.apiKeys.reverse();
        }
    }

    close(): void {
        this.hideSlideOverlay = true;
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.closeDialog();
        }
    }
}
